/**
 * CreateTaskModalV2 — V3-styled "Create Task" modal.
 *
 * Not a card. A modal form using the V3 design tokens (priority colors,
 * workarea colors, status morph, surface tones, gradient accent strip).
 *
 * Wired to Dashboard 2 click-to-create / drag-to-create flows only.
 *
 * Field set (data standard from V3 task card):
 *   title · work area · personal sub area (when work area is personal)
 *   priority · effort · status · responsible (owner)
 *   date · start time · end time · description
 *
 * onSave(payload) is called with a clean object. Caller is expected to
 * POST /api/tasks and refresh data; this modal is purely UI.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Dialog, DialogContent } from "../ui/dialog";
import { Calendar, Clock, X, Save, Edit3, Users } from "lucide-react";
import {
  V3_PRIORITIES,
  V3_EFFORTS,
  V3_STATUSES,
  V3_PERSONAL_SUB_AREAS,
  useV3TaskCardPreviewStyles,
} from "../design-system/OrkaTaskCardV5";

const cx = (...parts) => parts.filter(Boolean).join(" ");

const STATUS_KEY_TO_LEGACY = {
  todo: "To Do",
  inProgress: "In Progress",
  done: "Done",
  blocked: "Blocked",
};

const EFFORT_TO_MINUTES = { 1: 15, 2: 30, 3: 60, 5: 180, 8: 360, 13: 600 };

function addMinutesToTime(hhmm, mins) {
  if (!hhmm) return null;
  const [h, m] = hhmm.split(":").map((n) => parseInt(n, 10));
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  const total = h * 60 + m + mins;
  const eh = Math.floor((total % (24 * 60)) / 60);
  const em = total % 60;
  return `${String(eh).padStart(2, "0")}:${String(em).padStart(2, "0")}`;
}

export default function CreateTaskModalV2({
  open,
  onOpenChange,
  workAreas = [],
  defaultValues = null,
  onSave,
  people = [],
}) {
  // Inject the V3 preview stylesheet so token-select / status / etc. tokens
  // render with the same look as the v3 task card.
  useV3TaskCardPreviewStyles();

  const initial = useMemo(() => ({
    task_title: defaultValues?.task_title || "",
    task_description: defaultValues?.task_description || "",
    work_area_id: defaultValues?.work_area_id || workAreas[0]?.work_area_id || "",
    personal_sub_area_id: defaultValues?.personal_sub_area_id || "",
    task_priority: defaultValues?.task_priority ?? 3,
    task_estimated_effort: defaultValues?.task_estimated_effort ?? 3,
    task_status_key: defaultValues?.task_status_key || "todo",
    responsible_person_id: defaultValues?.responsible_person_id || "",
    task_delivery_date: defaultValues?.task_delivery_date || "",
    task_start_time: defaultValues?.task_start_time || "",
    task_end_time: defaultValues?.task_end_time || "",
  }), [defaultValues, workAreas]);

  const [draft, setDraft] = useState(initial);
  useEffect(() => { setDraft(initial); /* re-init when opening */ }, [open, initial]);

  const [statusMorphOpen, setStatusMorphOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const titleInputRef = useRef(null);
  useEffect(() => {
    if (open && titleInputRef.current) {
      const t = setTimeout(() => titleInputRef.current?.focus(), 60);
      return () => clearTimeout(t);
    }
  }, [open]);

  const update = (patch) => setDraft((prev) => {
    const next = { ...prev, ...patch };
    // Auto-derive end time when start + effort known and end empty.
    if (("task_start_time" in patch || "task_estimated_effort" in patch) && next.task_start_time && !next.task_end_time) {
      const minutes = EFFORT_TO_MINUTES[next.task_estimated_effort] || 60;
      next.task_end_time = addMinutesToTime(next.task_start_time, minutes) || "";
    }
    return next;
  });

  const wa = useMemo(
    () => workAreas.find((w) => w.work_area_id === draft.work_area_id) || workAreas[0] || null,
    [workAreas, draft.work_area_id],
  );
  const priorityMeta = V3_PRIORITIES.find((p) => p.value === draft.task_priority) || V3_PRIORITIES[4];
  const effortMeta = V3_EFFORTS.find((e) => e.value === draft.task_estimated_effort) || V3_EFFORTS[2];
  const statusMeta = V3_STATUSES.find((s) => s.value === draft.task_status_key) || V3_STATUSES[0];

  const cssVars = {
    "--priority-color": priorityMeta.color,
    "--workarea-color": wa?.color || "#64748b",
    "--status-color": statusMeta.color,
  };

  const isPersonalArea = !!wa?.is_personal;

  const canSave = !!(draft.task_title || "").trim() && !!draft.work_area_id;

  const handleSave = async () => {
    if (!canSave || saving) return;
    setSaving(true);
    try {
      const payload = {
        task_title: draft.task_title.trim(),
        task_description: draft.task_description || "",
        work_area_id: draft.work_area_id,
        task_priority: draft.task_priority,
        task_estimated_effort: draft.task_estimated_effort,
        task_status_key: draft.task_status_key,
        // Compat: many backend endpoints still read legacy task_status.
        task_status: STATUS_KEY_TO_LEGACY[draft.task_status_key] || "To Do",
        task_delivery_date: draft.task_delivery_date || null,
        task_start_time: draft.task_start_time || null,
        task_end_time: draft.task_end_time || null,
      };
      if (isPersonalArea && draft.personal_sub_area_id) {
        payload.personal_sub_area_id = draft.personal_sub_area_id;
      }
      if (draft.responsible_person_id) {
        payload.responsible_person_id = draft.responsible_person_id;
      }
      const ok = await Promise.resolve(onSave?.(payload));
      if (ok !== false) onOpenChange?.(false);
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl p-0 gap-0 border-0 bg-transparent shadow-none"
        data-testid="create-task-modal-v2"
      >
        <div className="orka-v3-scope" style={cssVars}>
          <div className="ctv2-shell" data-testid="ctv2-shell">
            {/* Top accent strip — gradient drawn from priority + work area */}
            <div className="ctv2-accent" aria-hidden="true" />

            <div className="ctv2-header">
              <div className="ctv2-eyebrow">
                <Edit3 className="w-3.5 h-3.5" /> NEW TASK
              </div>
              <button
                type="button"
                className="ctv2-close"
                aria-label="Close"
                onClick={() => onOpenChange?.(false)}
                data-testid="ctv2-close"
              >
                <X className="w-4 h-4" />
              </button>
              <input
                ref={titleInputRef}
                type="text"
                className="ctv2-title-input"
                placeholder="Untitled task — what needs to happen?"
                value={draft.task_title}
                onChange={(e) => update({ task_title: e.target.value })}
                data-testid="ctv2-title"
              />
              <div className="ctv2-meta-row">
                <label className="ctv2-meta-pill">
                  <Calendar className="w-3.5 h-3.5" />
                  <input
                    type="date"
                    value={draft.task_delivery_date || ""}
                    onChange={(e) => update({ task_delivery_date: e.target.value })}
                    data-testid="ctv2-date"
                  />
                </label>
                <label className="ctv2-meta-pill">
                  <Clock className="w-3.5 h-3.5" />
                  <input
                    type="time"
                    value={draft.task_start_time || ""}
                    onChange={(e) => update({ task_start_time: e.target.value })}
                    data-testid="ctv2-start-time"
                  />
                  <span className="ctv2-meta-sep">→</span>
                  <input
                    type="time"
                    value={draft.task_end_time || ""}
                    onChange={(e) => update({ task_end_time: e.target.value })}
                    data-testid="ctv2-end-time"
                  />
                </label>
              </div>
            </div>

            <div className="ctv2-grid">
              {/* Work area */}
              <div className="ctv2-field">
                <span className="ctv2-label">Work area</span>
                <label
                  className="token-select workarea-token ctv2-token"
                  style={{ "--token-color": wa?.color || "#64748b" }}
                >
                  <span className="token-dot" />
                  <select
                    value={draft.work_area_id}
                    onChange={(e) => update({ work_area_id: e.target.value, personal_sub_area_id: "" })}
                    data-testid="ctv2-work-area"
                  >
                    {workAreas.map((w) => (
                      <option key={w.work_area_id} value={w.work_area_id}>
                        {w.label || w.name || w.work_area_id}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              {/* Personal sub-area (only when personal) */}
              {isPersonalArea && (
                <div className="ctv2-field">
                  <span className="ctv2-label">Sub area</span>
                  <label
                    className="token-select subarea-token ctv2-token"
                    style={{ "--token-color": wa?.color || "#64748b" }}
                  >
                    <span className="token-dot" />
                    <select
                      value={draft.personal_sub_area_id}
                      onChange={(e) => update({ personal_sub_area_id: e.target.value })}
                      data-testid="ctv2-sub-area"
                    >
                      <option value="">—</option>
                      {V3_PERSONAL_SUB_AREAS.map((s) => (
                        <option key={s.sub_area_id} value={s.sub_area_id}>{s.label}</option>
                      ))}
                    </select>
                  </label>
                </div>
              )}

              {/* Priority */}
              <div className="ctv2-field">
                <span className="ctv2-label">Priority</span>
                <label
                  className="token-select priority-token ctv2-token"
                  style={{ "--token-color": priorityMeta.color }}
                >
                  <span className="token-dot" />
                  <select
                    value={draft.task_priority}
                    onChange={(e) => update({ task_priority: parseInt(e.target.value, 10) })}
                    data-testid="ctv2-priority"
                  >
                    {V3_PRIORITIES.map((p) => (
                      <option key={p.value} value={p.value}>{p.short} · {p.label}</option>
                    ))}
                  </select>
                </label>
              </div>

              {/* Effort */}
              <div className="ctv2-field">
                <span className="ctv2-label">Effort</span>
                <label
                  className="token-select effort-token ctv2-token"
                  style={{ "--token-color": effortMeta.color }}
                >
                  <span className="token-dot" />
                  <select
                    value={draft.task_estimated_effort}
                    onChange={(e) => update({ task_estimated_effort: parseInt(e.target.value, 10) })}
                    data-testid="ctv2-effort"
                  >
                    {V3_EFFORTS.map((eff) => (
                      <option key={eff.value} value={eff.value}>{eff.short} · {eff.label}</option>
                    ))}
                  </select>
                </label>
              </div>

              {/* Status — V3 morph */}
              <div className="ctv2-field ctv2-field-wide">
                <span className="ctv2-label">Status</span>
                {statusMorphOpen ? (
                  <div className="status-morph" style={{ "--status-color": statusMeta.color }} data-testid="ctv2-status-morph">
                    <div className="status-grid">
                      {V3_STATUSES.map((s) => (
                        <button
                          type="button"
                          key={s.value}
                          className={cx("status-choice", draft.task_status_key === s.value && "active")}
                          style={{ "--choice-color": s.color }}
                          onClick={() => { update({ task_status_key: s.value }); setStatusMorphOpen(false); }}
                          data-testid={`ctv2-status-${s.value}`}
                        >
                          <span className="status-dot" />
                          <span className="status-label">{s.label}</span>
                        </button>
                      ))}
                    </div>
                    <button
                      type="button"
                      className="status-morph-close"
                      onClick={() => setStatusMorphOpen(false)}
                      data-testid="ctv2-status-morph-close"
                    >
                      Close
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    className="status"
                    style={{ "--status-color": statusMeta.color }}
                    onClick={() => setStatusMorphOpen(true)}
                    data-testid="ctv2-status-toggle"
                  >
                    <span className="status-ring" />
                    <span>{statusMeta.label}</span>
                    <span style={{ marginLeft: "auto", opacity: 0.7, fontSize: 11, fontWeight: 800 }}>change</span>
                  </button>
                )}
              </div>

              {/* Responsible */}
              {people.length > 0 && (
                <div className="ctv2-field ctv2-field-wide">
                  <span className="ctv2-label"><Users className="w-3 h-3 inline -mt-0.5 mr-1" />Responsible</span>
                  <select
                    className="ctv2-select"
                    value={draft.responsible_person_id || ""}
                    onChange={(e) => update({ responsible_person_id: e.target.value })}
                    data-testid="ctv2-responsible"
                  >
                    <option value="">— Me —</option>
                    {people.map((p) => (
                      <option key={p.id || p.user_id || p.email} value={p.id || p.user_id}>
                        {p.name || p.full_name || p.email}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            {/* Description */}
            <div className="ctv2-description">
              <span className="ctv2-label">Description</span>
              <textarea
                className="ctv2-textarea"
                placeholder="Add context, links, or next steps…"
                value={draft.task_description}
                onChange={(e) => update({ task_description: e.target.value })}
                data-testid="ctv2-description"
              />
            </div>

            {/* Footer */}
            <div className="ctv2-footer">
              <button
                type="button"
                className="ctv2-cancel"
                onClick={() => onOpenChange?.(false)}
                data-testid="ctv2-cancel"
              >
                Cancel
              </button>
              <button
                type="button"
                className="ctv2-save"
                disabled={!canSave || saving}
                onClick={handleSave}
                data-testid="ctv2-save"
              >
                <Save className="w-4 h-4" />
                {saving ? "Saving…" : "Create task"}
              </button>
            </div>
          </div>

          <style>{`
            .ctv2-shell{
              position:relative;
              border-radius:22px;
              border:1px solid color-mix(in srgb,var(--priority-color) 24%,var(--orka-border-default,rgba(15,23,42,.12)));
              background:linear-gradient(180deg,
                color-mix(in srgb,var(--priority-color) 6%,var(--orka-surface-base,#fff)),
                var(--orka-surface-base,#fff));
              box-shadow:0 28px 64px rgba(15,23,42,.22);
              overflow:hidden;
              color:var(--orka-text-primary,#0f172a);
            }
            .ctv2-accent{
              height:6px;width:100%;
              background:linear-gradient(90deg,var(--priority-color),var(--workarea-color));
              opacity:.85;
            }
            .ctv2-header{position:relative;padding:18px 22px 14px;display:grid;gap:10px}
            .ctv2-eyebrow{
              display:inline-flex;align-items:center;gap:6px;
              font-size:10.5px;font-weight:950;letter-spacing:.12em;text-transform:uppercase;
              color:var(--workarea-color);
              background:color-mix(in srgb,var(--workarea-color) 10%,transparent);
              border:1px solid color-mix(in srgb,var(--workarea-color) 30%,transparent);
              border-radius:999px;padding:4px 9px;width:max-content;
            }
            .ctv2-close{
              position:absolute;top:14px;right:14px;
              width:30px;height:30px;border-radius:10px;
              border:1px solid var(--orka-border-default,rgba(15,23,42,.14));
              background:var(--orka-surface-raised,#f8fafc);
              color:var(--orka-text-tertiary,#64748b);
              display:grid;place-items:center;cursor:pointer;
            }
            .ctv2-close:hover{color:var(--orka-text-primary,#0f172a)}
            .ctv2-title-input{
              border:0;outline:0;background:transparent;
              font-size:24px;line-height:1.18;font-weight:950;letter-spacing:-.025em;
              color:var(--orka-text-primary,#0f172a);
              padding:2px 0;width:100%;
            }
            .ctv2-title-input::placeholder{color:var(--orka-text-tertiary,#94a3b8)}
            .ctv2-meta-row{display:flex;flex-wrap:wrap;gap:8px;margin-top:2px}
            .ctv2-meta-pill{
              display:inline-flex;align-items:center;gap:6px;
              height:30px;padding:0 10px;border-radius:999px;
              border:1px solid var(--orka-border-default,rgba(15,23,42,.12));
              background:var(--orka-surface-raised,#f8fafc);
              color:var(--orka-text-tertiary,#64748b);
              font-size:12px;font-weight:850;cursor:text;
            }
            .ctv2-meta-pill input{
              border:0;outline:0;background:transparent;
              color:var(--orka-text-primary,#0f172a);font:inherit;font-weight:900;
              min-width:0;padding:0;
            }
            .ctv2-meta-pill input::-webkit-calendar-picker-indicator{opacity:.6}
            .ctv2-meta-sep{color:var(--orka-text-tertiary,#94a3b8);font-weight:700}
            .ctv2-grid{
              display:grid;grid-template-columns:repeat(2,minmax(0,1fr));
              gap:12px;padding:6px 22px 14px;
            }
            @media(max-width:560px){.ctv2-grid{grid-template-columns:1fr}}
            .ctv2-field{display:grid;gap:6px;min-width:0}
            .ctv2-field-wide{grid-column:1 / -1}
            .ctv2-label{
              font-size:10px;text-transform:uppercase;letter-spacing:.08em;
              font-weight:950;color:var(--orka-text-tertiary,#64748b);
            }
            .ctv2-token{height:36px;padding:0 11px;font-size:13px}
            .ctv2-token select{font-size:13px;font-weight:900;padding-right:14px;width:100%}
            .ctv2-select{
              height:36px;padding:0 11px;font-size:13px;font-weight:850;
              border:1px solid var(--orka-border-default,rgba(15,23,42,.14));
              border-radius:10px;
              background:var(--orka-surface-raised,#f8fafc);
              color:var(--orka-text-primary,#0f172a);outline:0;width:100%;
            }
            .ctv2-description{padding:6px 22px 18px;display:grid;gap:6px}
            .ctv2-textarea{
              width:100%;min-height:96px;resize:vertical;
              border:1px solid var(--orka-border-default,rgba(15,23,42,.14));
              border-radius:12px;
              background:var(--orka-surface-raised,#f8fafc);
              color:var(--orka-text-primary,#0f172a);
              padding:12px;font-size:13px;line-height:1.5;outline:0;
            }
            .ctv2-textarea:focus{border-color:color-mix(in srgb,var(--priority-color) 50%,var(--orka-border-default))}
            .ctv2-footer{
              display:flex;justify-content:flex-end;gap:10px;
              padding:14px 22px 18px;
              border-top:1px solid var(--orka-border-default,rgba(15,23,42,.08));
              background:color-mix(in srgb,var(--workarea-color) 3%,var(--orka-surface-raised,#f8fafc));
            }
            .ctv2-cancel{
              height:40px;padding:0 16px;border-radius:10px;
              border:1px solid var(--orka-border-default,rgba(15,23,42,.14));
              background:transparent;color:var(--orka-text-secondary,#334155);
              font-size:13px;font-weight:900;cursor:pointer;
            }
            .ctv2-save{
              height:40px;padding:0 18px;border-radius:10px;border:0;
              background:linear-gradient(180deg,var(--priority-color),color-mix(in srgb,var(--priority-color) 72%,#000));
              color:#fff;font-size:13px;font-weight:950;cursor:pointer;
              display:inline-flex;align-items:center;gap:7px;
              box-shadow:0 12px 26px color-mix(in srgb,var(--priority-color) 30%,transparent);
            }
            .ctv2-save:disabled{opacity:.5;cursor:not-allowed;box-shadow:none}
            /* Status morph inside modal — give it sane height. */
            .orka-v3-scope .ctv2-shell .status-morph{min-height:auto}
            .orka-v3-scope .ctv2-shell .status{
              height:42px;border-radius:14px;font-size:14px;padding:0 14px;
              width:100%;justify-content:flex-start;gap:10px;
            }
            .orka-v3-scope .ctv2-shell .status-ring{width:18px;height:18px}
          `}</style>
        </div>
      </DialogContent>
    </Dialog>
  );
}
